import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Container, Btn } from '../components/ui';
import Seo from '../components/Seo';
import { signupStatus } from '../lib/marketingApi';

const MAX_POLLS = 8;

export default function SignupSuccess() {
  const params = new URLSearchParams(useLocation().search);
  const sessionId = params.get('session_id');
  const isFree = params.get('free') === '1';
  const freeCompany = params.get('company');
  const [state, setState] = useState(isFree ? 'paid' : 'checking'); // checking | paid | timeout | error
  const [info, setInfo] = useState(isFree ? { company: freeCompany, plan: 'Free' } : {});
  const polls = useRef(0);

  useEffect(() => {
    if (isFree) return;
    if (!sessionId) { setState('error'); return; }
    let active = true;
    const tick = async () => {
      try {
        const res = await signupStatus(sessionId);
        if (!active) return;
        setInfo(res);
        if (res.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired'].includes(res.payment_status)) { setState('error'); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('timeout'); return; }
        setTimeout(tick, 2000);
      } catch (e) {
        if (!active) return;
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('error'); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div data-testid="signup-success-page">
      <Seo title="Subscription Confirmation" noindex />
      <section className="px-5 py-20 sm:px-8 sm:py-28">
        <Container className="max-w-xl">
          <div className="flex flex-col items-center gap-5 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-12">
            {state === 'checking' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-brand" />
                <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Confirming your payment…</h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">Hang tight while we set up your BillPortal account.</p>
              </>
            )}
            {state === 'paid' && (
              <>
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300"><CheckCircle2 className="h-9 w-9" /></span>
                <h1 className="font-display text-3xl font-black text-slate-900 dark:text-white" data-testid="signup-success-title">You're all set{info.company ? `, ${info.company}` : ''}!</h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Your {info.plan ? <strong>{info.plan}</strong> : ''} subscription is active and your account has been created. Our onboarding team will reach out shortly to help you go live.
                </p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <Btn to="/platform/onboarding-planner" arrow>Explore the Onboarding Planner</Btn>
                  <Btn to="/" variant="outline">Back to home</Btn>
                </div>
              </>
            )}
            {(state === 'timeout') && (
              <>
                <Loader2 className="h-12 w-12 text-amber-500" />
                <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Almost there</h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">Your payment is processing. If it was successful, your account will be created within a few minutes and our team will be in touch.</p>
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">Back to home <ArrowRight className="h-4 w-4" /></Link>
              </>
            )}
            {state === 'error' && (
              <>
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300"><XCircle className="h-9 w-9" /></span>
                <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">We couldn't confirm your payment</h1>
                <p className="text-sm text-slate-600 dark:text-slate-300">If you completed checkout, don't worry — contact us and we'll sort it out. Otherwise you can try again.</p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                  <Btn to="/pricing">Try again</Btn>
                  <Btn to="/support" variant="outline">Contact support</Btn>
                </div>
              </>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
